import type { RiskLimitField, RiskLimits } from '../limits';
import type { AdvancedRuleFilter } from '../rules';
import type { LabRiskSettingsTab, RiskRuleDialogState, RiskRuleDraft } from './types';

export type LabRiskSettingsAction =
    | { type: 'SET_SCOPE'; firmId: string | undefined; superUserMode: boolean }
    | { type: 'SET_ACTIVE_TAB'; tab: LabRiskSettingsTab }
    | { type: 'SET_DEFAULT_ENABLED'; field: RiskLimitField; enabled: boolean }
    | { type: 'SET_DEFAULT_VALUE'; field: RiskLimitField; value: number | null }
    | { type: 'SYNC_SAVED_DEFAULTS'; defaults: RiskLimits; resetDraft: boolean }
    | { type: 'START_SAVE_DEFAULTS'; firmId: string }
    | { type: 'APPLY_SAVED_DEFAULTS'; defaults: RiskLimits; firmId: string }
    | { type: 'FAIL_SAVE_DEFAULTS'; firmId: string }
    | { type: 'OPEN_RULE_DIALOG'; dialog: RiskRuleDialogState }
    | { type: 'CLOSE_RULE_DIALOG' }
    | { type: 'PATCH_RULE_DRAFT'; patch: Partial<RiskRuleDraft> }
    | { type: 'START_SAVE_RULE' }
    | { type: 'FAIL_SAVE_RULE'; message: string }
    | { type: 'REQUEST_DELETE_RULE'; parameterId: string; token: number }
    | { type: 'CANCEL_DELETE_RULE' }
    | { type: 'START_DELETE_RULE' }
    | { type: 'FINISH_DELETE_RULE'; token: number }
    | { type: 'SET_ADVANCED_FILTER'; filter: AdvancedRuleFilter }
    | { type: 'SET_ADVANCED_SEARCH_INPUT'; value: string };

export const setScope = (firmId: string | undefined, superUserMode: boolean): LabRiskSettingsAction => ({
    type: 'SET_SCOPE',
    firmId,
    superUserMode,
});

export const setActiveTab = (tab: LabRiskSettingsTab): LabRiskSettingsAction => ({ type: 'SET_ACTIVE_TAB', tab });

export const setDefaultEnabled = (field: RiskLimitField, enabled: boolean): LabRiskSettingsAction => ({
    type: 'SET_DEFAULT_ENABLED',
    field,
    enabled,
});

export const setDefaultValue = (field: RiskLimitField, value: number | null): LabRiskSettingsAction => ({
    type: 'SET_DEFAULT_VALUE',
    field,
    value,
});

/** Refresh the server baseline; resets the draft only while the form is clean. */
export const syncSavedDefaults = (defaults: RiskLimits, resetDraft: boolean): LabRiskSettingsAction => ({
    type: 'SYNC_SAVED_DEFAULTS',
    defaults,
    resetDraft,
});

// The save lifecycle carries the firm it was started for so a request
// that resolves after the user switched firms can't touch the new
// firm's state.
export const startSaveDefaults = (firmId: string): LabRiskSettingsAction => ({ type: 'START_SAVE_DEFAULTS', firmId });

export const applySavedDefaults = (defaults: RiskLimits, firmId: string): LabRiskSettingsAction => ({
    type: 'APPLY_SAVED_DEFAULTS',
    defaults,
    firmId,
});

export const failSaveDefaults = (firmId: string): LabRiskSettingsAction => ({ type: 'FAIL_SAVE_DEFAULTS', firmId });

export const openRuleDialog = (dialog: RiskRuleDialogState): LabRiskSettingsAction => ({
    type: 'OPEN_RULE_DIALOG',
    dialog,
});

export const closeRuleDialog = (): LabRiskSettingsAction => ({ type: 'CLOSE_RULE_DIALOG' });

export const patchRuleDraft = (patch: Partial<RiskRuleDraft>): LabRiskSettingsAction => ({
    type: 'PATCH_RULE_DRAFT',
    patch,
});

export const startSaveRule = (): LabRiskSettingsAction => ({ type: 'START_SAVE_RULE' });

export const failSaveRule = (message: string): LabRiskSettingsAction => ({ type: 'FAIL_SAVE_RULE', message });

export const requestDeleteRule = (parameterId: string, token: number): LabRiskSettingsAction => ({
    type: 'REQUEST_DELETE_RULE',
    parameterId,
    token,
});

export const cancelDeleteRule = (): LabRiskSettingsAction => ({ type: 'CANCEL_DELETE_RULE' });

export const startDeleteRule = (): LabRiskSettingsAction => ({ type: 'START_DELETE_RULE' });

// The completion carries the confirmation token it was started for so a
// request that resolves after a firm switch — even one for the same
// rule, re-requested after switching back — can't close the wrong dialog.
export const finishDeleteRule = (token: number): LabRiskSettingsAction => ({
    type: 'FINISH_DELETE_RULE',
    token,
});

export const setAdvancedFilter = (filter: AdvancedRuleFilter): LabRiskSettingsAction => ({
    type: 'SET_ADVANCED_FILTER',
    filter,
});

export const setAdvancedSearchInput = (value: string): LabRiskSettingsAction => ({
    type: 'SET_ADVANCED_SEARCH_INPUT',
    value,
});
